import { EntityManager } from '../../engine/ecs/core/EntityManager';
import { SystemManager } from '../../engine/ecs/core/SystemManager';
import { ComponentManager } from '../../engine/ecs/core/ComponentManager';
import { EventManager } from '../../engine/events/EventManager';
import { EventListener } from '../../engine/events/EventListener';
import { Event } from '../../engine/events/Event';
import { Logger } from '../../engine/utils/Logger';
import { PerformanceMonitor } from '../../engine/utils/PerformanceMonitor';

// Import our new enhanced systems
import { AnimationSystem } from '../../engine/ecs/systems/AnimationSystem';
import { PlayerInputSystem } from '../../engine/ecs/systems/PlayerInputSystem';
import { RenderingSystem } from '../../engine/ecs/systems/RenderingSystem';

// Import Pacman-specific systems
import { PacmanMovementSystem } from './systems/PacmanMovementSystem';
import { GhostAISystem } from './systems/GhostAISystem';
import { CollectionSystem } from './systems/CollectionSystem';
import { GameStateSystem } from './systems/GameStateSystem';
import { UISystem } from './systems/UISystem';
import { PhysicsSystem } from './systems/PhysicsSystem';

// Import components for respawn functionality
import { PacmanComponent, Direction } from './components/PacmanComponent';
import { TransformComponent } from '../../engine/ecs/components/TransformComponent';
import { SpriteComponent } from '../../engine/ecs/components/SpriteComponent';
import { Vector2 } from '../../engine/ecs/core/Vector2';

// Import entity factories
import { PacmanEntityFactory } from './entities/PacmanEntityFactory';
import { GhostEntityFactory } from './entities/GhostEntityFactory';
import { MazeEntityFactory } from './entities/MazeEntityFactory';

// Import game data
import { MazeData } from './data/MazeData';
import { LevelConfigManager } from './data/LevelConfig';
import { ResourceManager } from '../../engine/resources/ResourceManager';
import { AudioSystem } from './systems/AudioSystem';

/**
 * Pacman game states
 */
export enum PacmanGameState {
  MainMenu = 'main_menu',
  Playing = 'playing',
  Paused = 'paused',
  PowerMode = 'power_mode',
  GameOver = 'game_over',
  LevelComplete = 'level_complete',
  LevelCompleteScreen = 'level_complete_screen',
  Victory = 'victory',
}

/**
 * PacmanGame - Professional implementation using our ECS architecture
 * Showcases the power of our game engine with beautiful graphics and smooth gameplay
 */
export class PacmanGame implements EventListener {
  private entityManager: EntityManager;
  private systemManager: SystemManager;
  private componentManager: ComponentManager;
  private eventManager: EventManager;
  private logger: Logger;
  private performanceMonitor: PerformanceMonitor;

  // Game state
  private currentState: PacmanGameState;
  private level: number;
  private score: number;
  private lives: number;
  private dotsRemaining: number;
  private powerModeTimer: number;

  // Entity factories
  private pacmanFactory: PacmanEntityFactory;
  private ghostFactory: GhostEntityFactory;
  private mazeFactory: MazeEntityFactory;

  // Rendering context
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;

  // Timing
  private lastFrameTime: number = 0;

  // Audio
  private resourceManager!: ResourceManager;
  private audioSystem!: AudioSystem;

  // Global level offset (for cross-world difficulty scaling)
  private levelOffset: number = 0;
  // Maze generation options
  private mazeMode: 'fixed' | 'random' = 'fixed';
  private mazeSeed: string | undefined;

  constructor(
    canvas: HTMLCanvasElement,
    options?: {
      levelOffset?: number;
      mazeMode?: 'fixed' | 'random';
      mazeSeed?: string;
    }
  ) {
    this.canvas = canvas;
    this.context = canvas.getContext('2d')!;

    // Initialize engine components
    this.entityManager = EntityManager.getInstance();
    this.systemManager = new SystemManager(); // SystemManager doesn't have getInstance
    this.componentManager = ComponentManager.getInstance();
    this.eventManager = EventManager.getInstance();
    this.logger = Logger.getInstance();
    this.performanceMonitor = PerformanceMonitor.getInstance();

    // Apply global level offset if provided
    if (options && typeof options.levelOffset === 'number') {
      this.levelOffset = Math.max(0, Math.floor(options.levelOffset));
    }

    // Apply maze generation options if provided (BEFORE using them)
    if (options?.mazeMode) {
      this.mazeMode = options.mazeMode;
    }
    if (options?.mazeSeed) {
      this.mazeSeed = options.mazeSeed;
    }

    // Set proper canvas dimensions for Pacman (use same options for size calculation)
    const mazeData = MazeData.getLevel(1, {
      mazeMode: this.mazeMode,
      mazeSeed: this.mazeSeed,
    });
    this.canvas.width = mazeData.width * mazeData.cellSize;
    this.canvas.height = mazeData.height * mazeData.cellSize;

    console.log(
      `[PacmanGame] Constructor - mazeMode: ${this.mazeMode}, mazeSeed: ${this.mazeSeed}`
    );

    // Initialize game state
    this.currentState = PacmanGameState.MainMenu;
    this.level = 1;
    this.score = 0;
    this.lives = 3;
    this.dotsRemaining = 0;
    this.powerModeTimer = 0;

    // Initialize entity factories
    this.pacmanFactory = new PacmanEntityFactory();
    this.ghostFactory = new GhostEntityFactory();
    this.mazeFactory = new MazeEntityFactory();

    this.initializeSystems();
    this.setupEventListeners();
    this.setupGlobalKeyListener();

    this.logger.info('PacmanGame: Initialized successfully');
  }

  /**
   * Initialize all game systems
   */
  private initializeSystems(): void {
    // Core engine systems
    this.systemManager.addSystem(new RenderingSystem(this.context));
    this.systemManager.addSystem(new AnimationSystem());
    this.systemManager.addSystem(new PlayerInputSystem());

    // Pacman-specific systems
    this.systemManager.addSystem(new PacmanMovementSystem());
    this.systemManager.addSystem(new GhostAISystem());
    this.systemManager.addSystem(new CollectionSystem());
    this.systemManager.addSystem(new GameStateSystem());
    this.systemManager.addSystem(new UISystem(this.context));

    // Physics system for enhanced effects
    this.systemManager.addSystem(new PhysicsSystem(this.canvas));

    // Audio system for sound effects and music
    this.resourceManager = ResourceManager.getInstance(null as any);
    this.resourceManager.setAssetsDirectory('GameAssets');
    this.audioSystem = new AudioSystem(this.resourceManager);
    this.systemManager.addSystem(this.audioSystem);

    this.logger.info('PacmanGame: All systems added to SystemManager');

    this.logger.info('PacmanGame: Systems initialized');
  }

  /**
   * Setup event listeners for game events
   */
  private setupEventListeners(): void {
    // We'll add event listeners for game-specific events
    this.eventManager.subscribe('dot_collected', this);
    this.eventManager.subscribe('power_pellet_collected', this);
    this.eventManager.subscribe('ghost_eaten', this);
    this.eventManager.subscribe('pacman_died', this);
    this.eventManager.subscribe('level_complete', this);
    this.eventManager.subscribe('game_over', this);
    // Mobile explicit controls via engine events
    this.eventManager.subscribe('mobile_toggle_pause', this);
    this.eventManager.subscribe('mobile_toggle_mute', this);
    // Listen to engine keydown so mobile keyboards/controls act like desktop
    this.eventManager.subscribe('keydown', this);

    // Test event system
    console.log('PacmanGame: Event listeners setup complete');
    console.log('PacmanGame: Subscribed to pacman_died event');

    // Test event publishing after a short delay to ensure game loop is running
    setTimeout(() => {
      console.log('PacmanGame: Testing event system...');
      this.eventManager.publish(new Event('test_event'));
      console.log(
        'PacmanGame: Test event published, should be processed in next frame'
      );
    }, 2000);

    this.logger.info('PacmanGame: Event listeners setup');
  }

  /**
   * Start the game
   */
  public start(): void {
    this.logger.info(`PacmanGame: Starting game at level ${this.level}`);

    // If we're already in a game state, just reinitialize the level
    if (this.currentState !== PacmanGameState.MainMenu) {
      this.logger.info('PacmanGame: Reinitializing level for next level');
      this.initializeLevel();

      // Force systems to update with new entities
      this.systemManager.update(0);

      this.changeState(PacmanGameState.Playing);

      // Debug: Check if entities are properly registered
      const totalEntities = this.entityManager.getAllEntities().length;
      console.log(
        `PacmanGame: After reinitialization - Total entities: ${totalEntities}`
      );

      // Don't start a new game loop if one is already running
      return;
    }

    // First time starting the game
    this.initializeLevel();
    this.changeState(PacmanGameState.Playing);
    try {
      (window as any).pacmanControls = {
        pause: () => this.togglePause(),
        mute: () => this.audioSystem?.toggleAudio(),
      };
    } catch {}
    this.gameLoop();
  }

  /**
   * Clear all entities from all systems
   */
  private clearAllSystemEntities(): void {
    const systems = this.systemManager.getSystems();
    for (const system of systems) {
      system.removeAllEntities();
    }
    this.logger.info('PacmanGame: Cleared all entities from all systems');
  }

  /**
   * Initialize a new level
   */
  private initializeLevel(): void {
    // Clear existing entities
    this.entityManager.clear();
    this.componentManager.clear(); // Also clear components to prevent orphaned references
    this.logger.info('PacmanGame: Cleared existing entities and components');

    // Force systems to clear their entity lists
    this.clearAllSystemEntities();

    // Get level-specific configuration
    const effectiveLevel = this.level + this.levelOffset;
    const levelConfig = LevelConfigManager.getLevelConfig(effectiveLevel);
    const ghostSpeed = LevelConfigManager.getGhostSpeed(effectiveLevel);
    const pacmanSpeed = LevelConfigManager.getPacmanSpeed(effectiveLevel);

    this.logger.info(
      `PacmanGame: Effective Level ${effectiveLevel} (local ${this.level}) config - Ghost speed: ${ghostSpeed}, Pacman speed: ${pacmanSpeed}`
    );

    // Create maze and register all entities with systems
    const mazeData = MazeData.getLevel(this.level, {
      mazeMode: this.mazeMode,
      mazeSeed: this.mazeSeed,
    });
    this.logger.info(
      `PacmanGame: Creating maze for level ${this.level} (mode: ${this.mazeMode}, seed: ${this.mazeSeed})`
    );
    this.logger.info(
      `PacmanGame: Maze dimensions: ${mazeData.width}x${mazeData.height}, cell size: ${mazeData.cellSize}`
    );

    // Update movement system with the correct maze data
    const movementSystem = this.systemManager.getSystem(PacmanMovementSystem);
    if (movementSystem) {
      movementSystem.setMazeData(mazeData);
      console.log('[PacmanGame] Updated PacmanMovementSystem with maze data');
    }

    // Update ghost AI system with the correct maze data
    const ghostAISystem = this.systemManager.getSystem(GhostAISystem);
    if (ghostAISystem) {
      ghostAISystem.setMazeData(mazeData);
      console.log('[PacmanGame] Updated GhostAISystem with maze data');
    }

    const mazeEntities = this.mazeFactory.createMaze(mazeData);

    // Register all maze entities with systems
    for (const entity of mazeEntities) {
      this.systemManager.addEntity(entity);
    }
    this.logger.info(
      `PacmanGame: Created ${mazeEntities.length} maze entities`
    );

    // Create Pacman and register with systems
    const pacmanStartPos = mazeData.pacmanStartPosition;
    this.logger.info(
      `PacmanGame: Creating Pacman at position (${pacmanStartPos.x}, ${pacmanStartPos.y})`
    );
    const pacmanEntity = this.pacmanFactory.createPacman(
      pacmanStartPos.x,
      pacmanStartPos.y,
      pacmanSpeed // Pass level-specific speed
    );
    this.systemManager.addEntity(pacmanEntity);

    // Create ghosts and register with systems
    const ghostStartPositions = mazeData.ghostStartPositions;
    this.logger.info(
      `PacmanGame: Creating ${ghostStartPositions.length} ghosts`
    );

    const blinky = this.ghostFactory.createBlinky(
      ghostStartPositions[0].x,
      ghostStartPositions[0].y,
      ghostSpeed // Pass level-specific speed
    );
    this.systemManager.addEntity(blinky);

    const pinky = this.ghostFactory.createPinky(
      ghostStartPositions[1].x,
      ghostStartPositions[1].y,
      ghostSpeed // Pass level-specific speed
    );
    this.systemManager.addEntity(pinky);

    const inky = this.ghostFactory.createInky(
      ghostStartPositions[2].x,
      ghostStartPositions[2].y,
      ghostSpeed // Pass level-specific speed
    );
    this.systemManager.addEntity(inky);

    const clyde = this.ghostFactory.createClyde(
      ghostStartPositions[3].x,
      ghostStartPositions[3].y,
      ghostSpeed // Pass level-specific speed
    );
    this.systemManager.addEntity(clyde);

    this.logger.info('PacmanGame: Created Pacman and 4 ghosts');

    // Count dots for level completion
    this.dotsRemaining = mazeData.totalDots;

    // Log total entities created
    const totalEntities = this.entityManager.getAllEntities().length;
    this.logger.info(`PacmanGame: Total entities in game: ${totalEntities}`);

    // Check rendering system entities
    const renderingSystem = this.systemManager.getSystem(RenderingSystem);
    if (renderingSystem) {
      const renderableEntities = renderingSystem.getEntities().length;
      this.logger.info(
        `PacmanGame: Entities in rendering system: ${renderableEntities}`
      );
    }

    // Check other systems - use proper class references
    // Note: We'll skip system-specific checks for now to avoid import issues
    this.logger.info(`PacmanGame: Level initialization complete`);

    this.logger.info(`PacmanGame: Level ${this.level} initialized`);
  }

  /**
   * Main game loop
   */
  private gameLoop(): void {
    // Start frame timing
    this.performanceMonitor.startFrame();

    // Calculate delta time manually
    const currentTime = performance.now();
    const deltaTime = this.lastFrameTime
      ? (currentTime - this.lastFrameTime) / 1000
      : 0.016;
    this.lastFrameTime = currentTime;

    // Update event system - process all queued events
    this.eventManager.update();

    // Handle different game states
    switch (this.currentState) {
      case PacmanGameState.Playing:
      case PacmanGameState.PowerMode:
        // Update all systems normally
        this.systemManager.update(deltaTime);
        this.updateGameLogic(deltaTime);
        break;

      case PacmanGameState.Paused: {
        const renderingSystem = this.systemManager.getSystem(RenderingSystem);
        if (renderingSystem) {
          renderingSystem.update(deltaTime);
        }
        break;
      }

      case PacmanGameState.LevelComplete:
      case PacmanGameState.LevelCompleteScreen:
        // Don't update game systems during level complete screen to prevent conflicts
        // But still update rendering system to show the level complete screen
        const renderingSystem = this.systemManager.getSystem(RenderingSystem);
        if (renderingSystem) {
          renderingSystem.update(deltaTime);
        }
        break;

      case PacmanGameState.GameOver:
        // Only update rendering system during game over
        const renderSystem = this.systemManager.getSystem(RenderingSystem);
        if (renderSystem) {
          renderSystem.update(deltaTime);
        }
        break;

      default:
        // Update all systems for other states
        this.systemManager.update(deltaTime);
        this.updateGameLogic(deltaTime);
        break;
    }

    // Render everything
    this.render();

    // End frame timing
    this.performanceMonitor.endFrame();

    // Continue game loop
    requestAnimationFrame(() => this.gameLoop());
  }

  /**
   * Update game-specific logic
   */
  private updateGameLogic(deltaTime: number): void {
    // Debug: Log occasionally to see if game loop is running
    if (Math.random() < 0.01) {
      // 1% of frames
      console.log(
        `PacmanGame: Game loop running, deltaTime: ${deltaTime.toFixed(3)}`
      );
    }

    // Update power mode timer
    if (this.currentState === PacmanGameState.PowerMode) {
      this.powerModeTimer -= deltaTime;
      if (this.powerModeTimer <= 0) {
        this.changeState(PacmanGameState.Playing);
      }
    }

    // Check game over condition
    if (this.lives <= 0) {
      this.changeState(PacmanGameState.GameOver);
    }

    // Handle level complete screen input (simplified for now)
    if (this.currentState === PacmanGameState.LevelCompleteScreen) {
      // For now, just wait for manual intervention
      // The level complete screen will be displayed
    }
  }

  /**
   * Handle input for level complete screen
   */
  private handleLevelCompleteInput(): void {
    // Check if ENTER key is pressed to continue to next level
    // This is a simple check that can be called from the game loop
    if (this.isEnterKeyPressed()) {
      console.log('PacmanGame: ENTER key detected, starting next level');
      this.startNextLevel();
    }
  }

  /**
   * Check if ENTER key is currently pressed
   */
  private isEnterKeyPressed(): boolean {
    // Simple check for ENTER key - this could be improved with a proper input system
    return false; // For now, return false to prevent automatic progression
  }

  /**
   * Add global key listener for level completion
   */
  private setupGlobalKeyListener(): void {
    // Audio + pause controls via keyboard
    document.addEventListener('keydown', (event) => {
      const k = event.key.toLowerCase();
      switch (k) {
        case 'm':
          this.audioSystem?.toggleAudio();
          break;
        case 'p':
        case 'escape':
          this.togglePause();
          break;
        case '[': {
          const v = this.audioSystem?.getSoundVolume?.() ?? 0;
          this.audioSystem?.setSoundVolume(Math.max(0, v - 0.1));
          break;
        }
        case ']': {
          const v = this.audioSystem?.getSoundVolume?.() ?? 0;
          this.audioSystem?.setSoundVolume(Math.min(1, v + 0.1));
          break;
        }
        case '-': {
          const v = this.audioSystem?.getMusicVolume?.() ?? 0;
          this.audioSystem?.setMusicVolume(Math.max(0, v - 0.1));
          break;
        }
        case '=': {
          const v = this.audioSystem?.getMusicVolume?.() ?? 0;
          this.audioSystem?.setMusicVolume(Math.min(1, v + 0.1));
          break;
        }
      }
    });

    // Mobile custom events (from on-screen controls)
    document.addEventListener('mobile:pause' as any, () => {
      this.togglePause();
    });
    document.addEventListener('mobile:mute' as any, () => {
      this.audioSystem?.toggleAudio();
    });
  }

  /** Toggle pause/resume */
  private togglePause(): void {
    if (
      this.currentState === PacmanGameState.Playing ||
      this.currentState === PacmanGameState.PowerMode
    ) {
      this.changeState(PacmanGameState.Paused);
      this.logger.info('PacmanGame: Game paused');
      try {
        alert('Game: Paused');
      } catch {}
    } else if (this.currentState === PacmanGameState.Paused) {
      this.changeState(PacmanGameState.Playing);
      this.logger.info('PacmanGame: Game resumed');
      try {
        alert('Game: Resumed');
      } catch {}
    }
  }

  /**
   * Render the game
   */
  private render(): void {
    // Let the RenderingSystem handle clearing and rendering
    // Systems handle their own rendering through the update call

    // Debug: Log entity count occasionally during gameplay
    if (this.currentState === PacmanGameState.Playing) {
      if (Math.random() < 0.01) {
        // 1% of frames
        const totalEntities = this.entityManager.getAllEntities().length;
        console.log(
          `PacmanGame: Rendering ${totalEntities} entities in Playing state`
        );
      }
    }
  }

  /**
   * Render level complete screen
   */
  private renderLevelCompleteScreen(): void {
    // Clear the canvas
    this.context.fillStyle = '#000000';
    this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Render level complete message
    this.context.save();
    this.context.fillStyle = '#FFFF00';
    this.context.font = '48px Arial';
    this.context.textAlign = 'center';

    const centerX = this.canvas.width / 2;
    const centerY = this.canvas.height / 2;

    this.context.fillText('LEVEL COMPLETE!', centerX, centerY - 50);
    this.context.fillText(`Score: ${this.score}`, centerX, centerY);
    this.context.fillText(`Level: ${this.level}`, centerX, centerY + 50);
    this.context.fillText('Press ENTER to continue', centerX, centerY + 100);

    this.context.restore();
  }

  /**
   * Change game state
   */
  private changeState(newState: PacmanGameState): void {
    this.logger.info(
      `PacmanGame: State change ${this.currentState} -> ${newState}`
    );
    this.currentState = newState;

    // Handle state-specific logic
    switch (newState) {
      case PacmanGameState.PowerMode:
        const powerPelletDuration = LevelConfigManager.getPowerPelletDuration(
          this.level
        );
        this.powerModeTimer = powerPelletDuration; // Use level-specific duration
        this.logger.info(
          `PacmanGame: Power mode activated for ${powerPelletDuration} seconds (Level ${this.level})`
        );
        break;
      case PacmanGameState.LevelComplete:
        // Stop the game loop temporarily during level transition
        this.logger.info(
          `PacmanGame: Level ${this.level} completed! Preparing for level ${
            this.level + 1
          }`
        );
        this.level++;
        // Transition to level complete screen state
        this.currentState = PacmanGameState.LevelCompleteScreen;
        break;
      case PacmanGameState.GameOver:
        this.logger.info('Game Over! Final Score: ' + this.score);
        break;
    }
  }

  /**
   * Handle game events - implements EventListener interface
   */
  public onEvent(event: Event): void {
    console.log(`PacmanGame: Received event: ${event.getType()}`);

    switch (event.getType()) {
      case 'keydown': {
        try {
          // @ts-ignore
          const k = (event as any).getKey?.().toLowerCase?.();
          if (k === 'p' || k === 'escape') {
            this.togglePause();
            return;
          }
          if (k === 'm') {
            this.audioSystem?.toggleAudio();
            return;
          }
        } catch {}
        break;
      }
      case 'mobile_toggle_pause':
        this.togglePause();
        break;
      case 'mobile_toggle_mute':
        this.audioSystem?.toggleAudio();
        break;
      case 'dot_collected':
        this.score += 10;
        this.dotsRemaining--;
        console.log(
          `PacmanGame: Dot collected! Score: ${this.score}, Dots remaining: ${this.dotsRemaining}`
        );
        break;
      case 'power_pellet_collected':
        this.score += 50;
        this.dotsRemaining--;
        this.changeState(PacmanGameState.PowerMode);
        console.log(`PacmanGame: Power pellet collected! Score: ${this.score}`);
        break;
      case 'ghost_eaten':
        this.score += 200;
        console.log(`PacmanGame: Ghost eaten! Score: ${this.score}`);
        break;
      case 'pacman_died':
        console.log(
          `PacmanGame: PACMAN_DIED EVENT RECEIVED! Current lives: ${this.lives}`
        );
        this.lives--;
        console.log(`PacmanGame: Lives decremented! New lives: ${this.lives}`);
        if (this.lives <= 0) {
          console.log('PacmanGame: No lives remaining, game over!');
          this.changeState(PacmanGameState.GameOver);
        } else {
          console.log('PacmanGame: Scheduling Pacman respawn in 2 seconds...');
          // Respawn Pacman after a delay
          setTimeout(() => {
            console.log(
              'PacmanGame: Respawn timer triggered, calling respawnPacman()'
            );
            this.respawnPacman();
          }, 1000);
        }
        break;
      case 'level_complete':
        this.changeState(PacmanGameState.LevelComplete);
        break;
      case 'game_over':
        this.changeState(PacmanGameState.GameOver);
        break;
      case 'test_event':
        console.log(
          'PacmanGame: Test event received! Event system is working.'
        );
        break;
      default:
        console.log(`PacmanGame: Unknown event type: ${event.getType()}`);
        break;
    }
  }

  /**
   * Get current game state for external access
   */
  public getGameState(): PacmanGameState {
    return this.currentState;
  }

  /**
   * Get current score
   */
  public getScore(): number {
    return this.score;
  }

  /**
   * Get current level
   */
  public getLevel(): number {
    return this.level;
  }

  /**
   * Get remaining lives
   */
  public getLives(): number {
    return this.lives;
  }

  /**
   * Get current FPS
   */
  public getFPS(): number {
    return this.performanceMonitor.getMetrics().fps;
  }

  /**
   * Get current entity count
   */
  public getEntityCount(): number {
    return this.entityManager.getAllEntities().length;
  }

  /**
   * Start the next level - called from UI after level completion
   */
  public startNextLevel(): void {
    this.logger.info(`PacmanGame: Starting level ${this.level}`);
    console.log(
      `PacmanGame: Current state before startNextLevel: ${this.currentState}`
    );

    // Initialize the new level
    this.initializeLevel();

    // Debug: Check entity count after initialization
    const totalEntities = this.entityManager.getAllEntities().length;
    console.log(
      `PacmanGame: After level init - Total entities: ${totalEntities}`
    );

    // Ensure all systems are properly updated with new entities
    this.systemManager.update(0); // Force a system update with deltaTime 0

    // Change state to playing
    this.changeState(PacmanGameState.Playing);
    console.log(`PacmanGame: State after changeState: ${this.currentState}`);

    // Debug: Check entity count after state change
    const entitiesAfterStateChange = this.entityManager.getAllEntities().length;
    console.log(
      `PacmanGame: After state change - Total entities: ${entitiesAfterStateChange}`
    );

    this.logger.info(`PacmanGame: Level ${this.level} started successfully`);
  }

  /**
   * Respawn Pacman after death
   */
  private respawnPacman(): void {
    console.log('PacmanGame: respawnPacman() called');

    // Find Pacman entity and reset its state but keep position
    const allEntities = this.entityManager.getAllEntities();
    let pacmanFound = false;

    for (const entity of allEntities) {
      if (this.componentManager.hasComponent(entity, PacmanComponent)) {
        pacmanFound = true;
        const pacman = this.componentManager.getComponent(
          entity,
          PacmanComponent
        ) as PacmanComponent;
        const transform = this.componentManager.getComponent(
          entity,
          TransformComponent
        ) as TransformComponent;

        console.log(
          `PacmanGame: Found Pacman entity, current isAlive: ${pacman.isAlive}, position: (${transform.position.x}, ${transform.position.y})`
        );

        // Reset Pacman state but keep current position
        pacman.isAlive = true;
        pacman.currentDirection = Direction.None;
        pacman.nextDirection = Direction.None;
        pacman.invulnerabilityTimer = 3.0; // 3 seconds of invincibility after respawn

        console.log(
          `PacmanGame: Reset Pacman state, new isAlive: ${pacman.isAlive}, invincibility: ${pacman.invulnerabilityTimer}s, keeping position at (${transform.position.x}, ${transform.position.y})`
        );

        // DON'T reset position - keep Pacman where he got hit
        // This allows Pacman to continue from where he was hit, not from the beginning

        this.logger.info('Pacman respawned at current position');
        return;
      }
    }

    if (!pacmanFound) {
      console.error('PacmanGame: No Pacman entity found for respawn!');
    }
  }
}
